import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import type { ProductType } from '@/shared/lib/product-type'

/**
 * Параметры для получения списка отзывов
 */
export interface GetReviewsParams {
  productId: string
  productType: ProductType
  rating?: number
  sort?: 'date-desc' | 'date-asc' | 'rating-desc' | 'rating-asc' | 'helpful-desc'
  page?: number
  limit?: number
}

/**
 * Отзыв с сервера
 */
export interface Review {
  id: string
  userId: number
  userName: string
  userPhoto?: string
  productId: string
  productType: ProductType
  rating: number
  title?: string
  comment?: string
  images?: string[]
  isVerifiedPurchase: boolean
  helpfulCount: number
  createdAt: string
  updatedAt: string
}

/**
 * Распределение оценок
 */
export interface RatingDistribution {
  5: number
  4: number
  3: number
  2: number
  1: number
}

/**
 * Сводка по отзывам
 */
export interface ReviewsSummary {
  averageRating: number
  totalReviews: number
  ratingDistribution: RatingDistribution
}

/**
 * Ответ API для списка отзывов
 */
export interface ReviewsResponse {
  data: Review[]
  meta?: {
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
      hasNext: boolean
      hasPrev: boolean
    }
    summary: ReviewsSummary
  }
}

/**
 * Запрос на создание отзыва
 */
export interface CreateReviewRequest {
  productId: string
  productType: ProductType
  rating: number
  title?: string
  comment?: string
  images?: string[]
}

/**
 * Хук для получения списка отзывов
 * 
 * Использует TanStack Query для кэширования. Не требует авторизации.
 * 
 * @param params - Параметры фильтрации и сортировки
 * @returns Query результат с данными отзывов и сводкой
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useReviews({
 *   productId: 'product-1',
 *   productType: 'tea',
 *   sort: 'rating-desc',
 *   page: 1,
 *   limit: 10,
 * })
 * const reviews = data?.data
 * const summary = data?.meta?.summary
 * ```
 */
export function useReviews(params: GetReviewsParams) {
  return useQuery({
    queryKey: ['reviews', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      searchParams.append('productId', params.productId)
      searchParams.append('productType', params.productType)
      if (params.rating) searchParams.append('rating', params.rating.toString())
      if (params.sort) searchParams.append('sort', params.sort)
      if (params.page) searchParams.append('page', params.page.toString())
      if (params.limit) searchParams.append('limit', params.limit.toString())

      const queryString = searchParams.toString()
      const endpoint = `/api/reviews?${queryString}`
      
      return apiClient<ReviewsResponse>(endpoint)
    },
    enabled: !!params.productId && !!params.productType,
  })
}

/**
 * Хук для создания отзыва
 * 
 * Использует TanStack Query Mutation. После успешного создания автоматически
 * инвалидирует кэш отзывов для обновления данных.
 * 
 * @returns Mutation объект с методами mutate, mutateAsync и состоянием
 * 
 * @example
 * ```tsx
 * const createReview = useCreateReview()
 * 
 * const handleSubmit = () => {
 *   createReview.mutate({
 *     productId: 'product-1',
 *     productType: 'tea',
 *     rating: 5,
 *     title: 'Отличный чай!',
 *     comment: 'Очень ароматный и вкусный.',
 *   })
 * }
 * ```
 */
export function useCreateReview() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateReviewRequest) => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }
      return apiClient<{ data: Review }>('/api/reviews', {
        method: 'POST',
        body: JSON.stringify(data),
        initData,
      })
    },
    onSuccess: (_, variables) => {
      // Инвалидируем кэш отзывов для этого товара
      queryClient.invalidateQueries({
        queryKey: ['reviews', { productId: variables.productId }],
      })
    },
  })
}


